import type { AnswersByQuestion, ContentItem, PlaceholderValues } from "../layout/documentLayout";
import {
  answerAsString,
  DIGITAL_SIGNATURE_CONSENT_MARKER,
  formatAmericanDate,
  formatWeight,
  isWeightAnswer,
  readField,
  replaceInAnswer,
  stringItems,
} from "./answerFormat";
import { BODY_DRAWING_VIEWS, bodyDrawingViewValue, type BodyDrawingView } from "./bodyDrawingValue";

// Pure formatting for the table, signature and media blocks of the review-document preview,
// mirroring `mfDocumentAdapter.ts`. Node-test friendly (no React / app runtime imports).

type ItemType = ContentItem["itemType"];

// ---- Tables ----

/** Item types the PDF renders as chart columns (mfDocumentAdapter canBeChildOfChart). */
export function canBeChildOfChart(itemType: ItemType): boolean {
  switch (itemType) {
    case "radio":
    case "check":
    case "dropDown":
    case "yesNo":
    case "textShort":
    case "textLong":
    case "number":
    case "time":
    case "date":
    case "vbpItem":
    case "bloodPressure":
      return true;
    case "bigHeader":
    case "patientSignature":
    case "employeeSignature":
    case "customSignature":
    case "medicationProfile":
    case "medicationProfileItem":
    case "icd":
    case "icdCodes":
    case "icdChart":
    case "sectionedIcdCodes":
    case "finalIcdCodes":
    case "smallHeader":
    case "editor":
    case "chart":
    case "image":
    case "bodyDrawing":
    case "patientPhysician":
    case "POC":
    case "RNPlatformPatientPhysician":
    case "medicalDiagnosis":
    case "weight":
      return false;
    default: {
      const unhandled: never = itemType;
      return unhandled;
    }
  }
}

/**
 * A LocalDate as the PDF prints it in a table (formatAmericanLocalDate, M/d/yyyy); a chart date
 * cell that is not a LocalDate prints as is, an empty or non-text one as "".
 */
export function formatPdfTableDate(value: unknown): string {
  return typeof value !== "string" || value === "" ? "" : formatPdfLocalDate(value);
}

/** A LocalDate (js-joda or "yyyy-MM-dd" text) as the PDF prints it: M/d/yyyy; null -> "". */
export function formatPdfLocalDate(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  return match === null ? text : `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
}

export function formatChartCell(answer: unknown, itemType: ItemType): string {
  if (answer === null || answer === undefined) {
    return "";
  }
  if (itemType === "date") {
    return formatPdfTableDate(answer);
  }
  if (itemType === "bloodPressure") {
    return Array.isArray(answer) ? stringItems(answer).join(" / ") : "";
  }
  if (Array.isArray(answer)) {
    return stringItems(answer).join(", ");
  }
  if (isWeightAnswer(answer)) {
    return formatWeight(answer);
  }
  return answerAsString(answer) ?? "";
}

/**
 * One row per chartRow present in any column, ascending, cells in column order ("" where a column
 * has no answer in the row): the PDF's rows (mfDocumentAdapter buildChartTableRows).
 */
export function buildChartRows(
  columns: ContentItem[],
  answers: AnswersByQuestion,
  placeholders: PlaceholderValues
): string[][] {
  const chartRows = new Set<number>();
  for (const column of columns) {
    for (const answer of answers.get(column.id) ?? []) {
      chartRows.add(answer.chartRow);
    }
  }
  return [...chartRows]
    .sort((a, b) => a - b)
    .map((chartRow) =>
      columns.map((column) => {
        const answer = answers.get(column.id)?.find((x) => x.chartRow === chartRow)?.answer;
        return formatChartCell(replaceInAnswer(answer, placeholders), column.itemType);
      })
    );
}

/** The PDF's columns of an icdCodes / finalIcdCodes table (mfDocumentAdapter convertIcdToMfBlock). */
export const ICD_HEADINGS = ["Type", "DX Code", "Short description", "Start date", "End date"];

/**
 * icd* answers: Type / DX Code / Short description / Start date / End date. Dates as the GENERAL
 * PDF prints them (M/d/yyyy) in the "preview" layout; a template document ("list") prints them
 * with its own template, shown as MM/dd/yyyy.
 */
export function buildIcdRows(answer: unknown, layout: "list" | "preview" = "preview"): string[][] {
  if (!Array.isArray(answer)) {
    return [];
  }
  const formatDate = layout === "preview" ? formatPdfLocalDate : formatAmericanDate;
  return (answer as unknown[]).map((code) => [
    answerAsString(readField(code, "type")) ?? "OTHER",
    answerAsString(readField(code, "formattedDxCode")) ?? "",
    answerAsString(readField(code, "shortDesc")) ?? "",
    formatDate(readField(code, "startDate")),
    formatDate(readField(code, "endDate")),
  ]);
}

export function formatPocSchedule(attributes: unknown): string {
  const type = readField(attributes, "type");
  switch (type) {
    case "EveryVisit":
      return "Every Visit";
    case "OnDaysOfWeek":
      return `${String(
        readField(readField(attributes, "frequency"), "frequency")
      )} visits per week`;
    case "AsRequestedByPatient":
      return "As Requested";
    case "Daily":
      return "Daily";
    case "Weekly":
      return "Weekly";
    case "1-2xWeekly":
      return "1-2x Weekly";
    case "DailyAsNeeded":
      return "Daily as needed";
    case "WeeklyAsNeeded":
      return "Weekly as needed";
    case "1-2xWeeklyAsNeeded":
      return "1-2 times weekly as needed";
    default:
      return "Unknown";
  }
}

/** POC answers: Service (with plan-of-care code) / Schedule / Notes. */
export function buildPocRows(question: ContentItem, answer: unknown): string[][] {
  if (!Array.isArray(answer)) {
    return [];
  }
  return (answer as unknown[]).map((item) => {
    const name = answerAsString(readField(item, "item")) ?? "";
    const code = question.POCItems?.find((x) => x.name === name)?.planOfCareItemCode;
    return [
      code === undefined ? name : `${name} (${code})`,
      formatPocSchedule(readField(item, "attributes")),
      answerAsString(readField(item, "notes")) ?? "",
    ];
  });
}

// ---- Signatures / media ----

export type SignatureValue =
  | { type: "empty" }
  | { type: "image"; url: string }
  | { type: "consent" }
  | { type: "text"; text: string };

// mfDocumentAdapter isImageString, character for character: only a data URL with a plain
// `type/subtype` media type prints as an image (so not e.g. `image/svg+xml`, nor an http(s) URL).
const PDF_IMAGE_STRING =
  /^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i;

/** Whether the PDF prints a signature answer as an image (anything else prints as text). */
export function isImageSource(value: string): boolean {
  return PDF_IMAGE_STRING.test(value);
}

export function resolveSignature(answer: unknown): SignatureValue {
  const value = answerAsString(answer) ?? "";
  if (value === "") {
    return { type: "empty" };
  }
  if (value === DIGITAL_SIGNATURE_CONSENT_MARKER) {
    return { type: "consent" };
  }
  return isImageSource(value) ? { type: "image", url: value } : { type: "text", text: value };
}

/** Removes the inline font styles the PDF strips from editor HTML (renderDocument.ts). */
export function stripUnwantedStyles(html: string): string {
  return html.replace(/((font-size|font-family|line-height):.*?;)/g, "");
}

const BODY_DRAWING_BASE_URL = "https://medflyt-assets.s3.amazonaws.com/patient-docs/images";
export const BODY_DRAWING_LEGEND_URL = `${BODY_DRAWING_BASE_URL}/pain-diagram-legend.png`;

const BODY_DRAWING_BASE_IMAGES: Record<BodyDrawingView, string> = {
  FRONT: `${BODY_DRAWING_BASE_URL}/anatomy-front.jpg`,
  RIGHT: `${BODY_DRAWING_BASE_URL}/anatomy-right.jpg`,
  BACK: `${BODY_DRAWING_BASE_URL}/anatomy-back.jpg`,
  LEFT: `${BODY_DRAWING_BASE_URL}/anatomy-left.jpg`,
};

/** The PDF's default image of an untouched view (mfDocumentAdapter convertBodyDrawingToMfBlock). */
export function bodyDrawingBaseImage(view: BodyDrawingView): string {
  return BODY_DRAWING_BASE_IMAGES[view];
}

/** Stored as [front, right, left, back]; printed FRONT, RIGHT, BACK, LEFT with default images. */
export function bodyDrawingImages(answer: unknown): { caption: BodyDrawingView; url: string }[] {
  return BODY_DRAWING_VIEWS.map((view) => {
    const value = bodyDrawingViewValue(answer, view);
    return { caption: view, url: value === "" ? bodyDrawingBaseImage(view) : value };
  });
}
